import React, { createContext, useContext, ReactNode, useState, useEffect } from 'react';
import { UIUserRecommendation, MyCompanyType, MyPositionType, FilterRecommendationsInput } from 'types';

type RecommendationsContextType = {
	recommendedCandidates: UIUserRecommendation[];
	connectedCandidates: UIUserRecommendation[];
	inPipelineCandidates: UIUserRecommendation[];
};

export interface RecommendationsContextOptions {
	recommendations: RecommendationsContextType;
	positions: MyPositionType[];
	selectedPositionId?: string;
	companies: MyCompanyType[];
	activeCompanies: MyCompanyType[];
	selfCompanies: MyCompanyType[];
	selectedCompany: MyCompanyType;
	isLoadingRecommendations: boolean;
	isLoadingPositions: boolean;
	isLoadingCompanies: boolean;
	filterApplied: boolean;
	isFilterLoading: boolean;
	totalCompaniesCount: number;
	totalSelfCompaniesCount: number;
	totalActiveCompaniesCount: number;
	companyLocationFilterData: string[];
	recommendationsFilters: FilterRecommendationsInput;
	setIsLoadingPositions: React.Dispatch<React.SetStateAction<boolean>>;
	setSelectedPositionId: React.Dispatch<React.SetStateAction<string | undefined>>;
	setIsLoadingCompanies: React.Dispatch<React.SetStateAction<boolean>>;
	setIsLoadingRecommendations: React.Dispatch<React.SetStateAction<boolean>>;
	setRecommendations: React.Dispatch<React.SetStateAction<RecommendationsContextType>>;
	setCompanies: React.Dispatch<React.SetStateAction<MyCompanyType[]>>;
	setActiveCompanies: React.Dispatch<React.SetStateAction<MyCompanyType[]>>;
	setSelfCompanies: React.Dispatch<React.SetStateAction<MyCompanyType[]>>;
	setSelectedCompany: React.Dispatch<React.SetStateAction<MyCompanyType>>;
	setPositions: React.Dispatch<React.SetStateAction<MyPositionType[]>>;
	setFilterApplied: React.Dispatch<React.SetStateAction<boolean>>;
	setIsFilterLoading: React.Dispatch<React.SetStateAction<boolean>>;
	setTotalCompaniesCount: React.Dispatch<React.SetStateAction<number>>;
	setTotalSelfCompaniesCount: React.Dispatch<React.SetStateAction<number>>;
	setTotalActiveCompaniesCount: React.Dispatch<React.SetStateAction<number>>;
	setCompanyLocationFilterData: React.Dispatch<React.SetStateAction<string[]>>;
	setRecommendationsFilters: React.Dispatch<React.SetStateAction<FilterRecommendationsInput>>;
}
export interface RecommendationsContextProps {
	children: ReactNode;
}

const RecommendationsContext = createContext<RecommendationsContextOptions>({
	recommendations: { connectedCandidates: [], inPipelineCandidates: [], recommendedCandidates: [] },
	companies: [],
	activeCompanies: [],
	selfCompanies: [],
	selectedCompany: {} as MyCompanyType,
	isLoadingCompanies: false,
	isLoadingPositions: false,
	isLoadingRecommendations: false,
	positions: [],
	filterApplied: false,
	isFilterLoading: false,
	totalCompaniesCount: 0,
	totalSelfCompaniesCount: 0,
	totalActiveCompaniesCount: 0,
	companyLocationFilterData: [],
	recommendationsFilters: {} as FilterRecommendationsInput,
	setCompanies: () => {},
	setSelfCompanies: () => {},
	setActiveCompanies: () => {},
	setSelectedCompany: () => {},
	setIsLoadingCompanies: () => {},
	setIsLoadingPositions: () => {},
	setIsLoadingRecommendations: () => {},
	setPositions: () => {},
	setRecommendations: () => {},
	setSelectedPositionId: () => {},
	setFilterApplied: () => {},
	setIsFilterLoading: () => {},
	setTotalCompaniesCount: () => {},
	setTotalSelfCompaniesCount: () => {},
	setTotalActiveCompaniesCount: () => {},
	setCompanyLocationFilterData: () => {},
	setRecommendationsFilters: () => {},
});

export const RecommendationsContextProvider = ({ children }: RecommendationsContextProps) => {
	const [companies, setCompanies] = useState<MyCompanyType[]>([]);
	const [activeCompanies, setActiveCompanies] = useState<MyCompanyType[]>([]);
	const [selfCompanies, setSelfCompanies] = useState<MyCompanyType[]>([]);
	const [positions, setPositions] = useState<MyPositionType[]>([]);
	const [totalCompaniesCount, setTotalCompaniesCount] = useState<number>(0);
	const [totalSelfCompaniesCount, setTotalSelfCompaniesCount] = useState<number>(0);
	const [totalActiveCompaniesCount, setTotalActiveCompaniesCount] = useState<number>(0);
	const [selectedPositionId, setSelectedPositionId] = useState<string>();
	const [filterApplied, setFilterApplied] = useState<boolean>(false);
	const [companyLocationFilterData, setCompanyLocationFilterData] = useState<string[]>([]);
	const [isFilterLoading, setIsFilterLoading] = useState<boolean>(false);
	const [selectedCompany, setSelectedCompany] = useState<MyCompanyType>({} as MyCompanyType);
	const [isLoadingPositions, setIsLoadingPositions] = useState<boolean>(false);
	const [isLoadingCompanies, setIsLoadingCompanies] = useState<boolean>(false);
	const [isLoadingRecommendations, setIsLoadingRecommendations] = useState<boolean>(false);
	const [recommendationsFilters, setRecommendationsFilters] = useState<FilterRecommendationsInput>(
		{} as FilterRecommendationsInput,
	);
	const [recommendations, setRecommendations] = useState<RecommendationsContextType>({
		recommendedCandidates: [],
		connectedCandidates: [],
		inPipelineCandidates: [],
	});

	useEffect(() => {
		if (positions.length > 0) setSelectedPositionId(positions[0].position._id);
	}, [positions]);

	return (
		<RecommendationsContext.Provider
			value={{
				companies,
				activeCompanies,
				selfCompanies,
				selectedCompany,
				isLoadingCompanies,
				isLoadingPositions,
				isLoadingRecommendations,
				positions,
				recommendations,
				selectedPositionId,
				filterApplied,
				isFilterLoading,
				totalCompaniesCount,
				totalSelfCompaniesCount,
				totalActiveCompaniesCount,
				companyLocationFilterData,
				recommendationsFilters,
				setSelectedCompany,
				setSelectedPositionId,
				setCompanies,
				setActiveCompanies,
				setSelfCompanies,
				setIsLoadingCompanies,
				setIsLoadingPositions,
				setIsLoadingRecommendations,
				setPositions,
				setRecommendations,
				setFilterApplied,
				setIsFilterLoading,
				setTotalCompaniesCount,
				setTotalSelfCompaniesCount,
				setTotalActiveCompaniesCount,
				setRecommendationsFilters,
				setCompanyLocationFilterData,
			}}
		>
			{children}
		</RecommendationsContext.Provider>
	);
};

export const useRecommendationsContext = () => {
	const context = useContext(RecommendationsContext);
	return context;
};
